import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid } from "@mui/material";
import { useRef } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";

import { BlFormCheckbox } from "Forms/Components/BlFormCheckbox";
import { BlFormDropdown } from "Forms/Components/BlFormDropdown";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { BlPhoneInput } from "Forms/Components/BlPhoneInput";
import { useDefaultValues } from "Forms/Hooks/useDefaultValues";
import useHandleServerError from "Forms/Hooks/useHandleServerError";
import { getCountryOptions } from "Forms/Utils/EnumOptions";
import { getPostalCodeMaskRegex } from "Forms/Utils/postalCodeUtils";
import type { PartnerDetailDto } from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  type PartnerFormData,
  partnerFormSchema,
} from "Partners/Forms/partnerFormSchema";
import type { BlOption } from "Shared/Inputs/BlOption";

export interface PartnerFormHandle {
  submit: (
    onValid: (data: PartnerFormData) => void | Promise<void>,
  ) => () => void;
  reset: () => void;
}

export interface PartnerPrefillData {
  companyName?: string;
  email?: string;
  phone?: string;
}

interface PartnerFormProps {
  partner?: PartnerDetailDto | null;
  superiorPartnerOptions: BlOption[];
  formRef: React.RefObject<PartnerFormHandle | null>;
  serverError?: Error | null;
  lockedParentPartnerID?: string;
  prefill?: PartnerPrefillData;
}

export const PartnerForm = ({
  partner,
  superiorPartnerOptions,
  formRef,
  serverError,
  lockedParentPartnerID,
  prefill,
}: PartnerFormProps): React.ReactNode => {
  const { t } = useResource();

  const internalFormRef = useRef<UseFormReturn<PartnerFormData> | null>(null);

  const defaultValues = useDefaultValues(
    () => buildDefaults(partner, lockedParentPartnerID, prefill),
    [partner, lockedParentPartnerID, prefill],
    internalFormRef,
  );

  const form = useForm<PartnerFormData>({
    resolver: zodResolver(partnerFormSchema),
    defaultValues,
  });

  internalFormRef.current = form;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = form;

  useHandleServerError({
    form,
    error: serverError,
    formKey: "businessName",
    resource: Resources.ApiErrors.SavePartner,
  });

  // Expose submit/reset to parent via formRef
  formRef.current = {
    submit: (onValid) => () => handleSubmit(onValid)(),
    reset: () => reset(),
  };

  const watchedCountry = watch("country");

  return (
    <Grid container spacing={Spacing.M}>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="businessName"
          label={t(Resources.Partners.CreatePartnerModal.BusinessName)}
          errors={errors}
          placeholder={t(
            Resources.Partners.CreatePartnerModal.BusinessNamePlaceholder,
          )}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="superiorPartnerId"
          label={t(Resources.Partners.CreatePartnerModal.SuperiorPartner)}
          options={superiorPartnerOptions}
          errors={errors}
          placeholder={t(
            Resources.Partners.CreatePartnerModal.SuperiorPartnerPlaceholder,
          )}
          disabled={!!lockedParentPartnerID}
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="identificationNumber"
          label={t(Resources.Partners.CreatePartnerModal.IdentificationNumber)}
          errors={errors}
          placeholder={t(
            Resources.Partners.CreatePartnerModal
              .IdentificationNumberPlaceholder,
          )}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="vat"
          label={t(Resources.Partners.CreatePartnerModal.VAT)}
          errors={errors}
          placeholder={t(Resources.Partners.CreatePartnerModal.VATPlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="email"
          label={t(Resources.Partners.CreatePartnerModal.Email)}
          errors={errors}
          placeholder={t(
            Resources.Partners.CreatePartnerModal.EmailPlaceholder,
          )}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlPhoneInput
          control={control}
          name="phone"
          errors={errors}
          label={t(Resources.Partners.CreatePartnerModal.Phone)}
          placeholder={t(
            Resources.Partners.CreatePartnerModal.PhonePlaceholder,
          )}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="country"
          label={t(Resources.Partners.CreatePartnerModal.Country)}
          options={getCountryOptions()}
          errors={errors}
          placeholder={t(
            Resources.Partners.CreatePartnerModal.CountryPlaceholder,
          )}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="address"
          label={t(Resources.Partners.CreatePartnerModal.Address)}
          errors={errors}
          placeholder={t(
            Resources.Partners.CreatePartnerModal.AddressPlaceholder,
          )}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="city"
          label={t(Resources.Partners.CreatePartnerModal.City)}
          errors={errors}
          placeholder={t(Resources.Partners.CreatePartnerModal.CityPlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="postalCode"
          label={t(Resources.Partners.CreatePartnerModal.PostalCode)}
          errors={errors}
          placeholder={t(
            Resources.Partners.CreatePartnerModal.PostalCodePlaceholder,
          )}
          maskProps={{ mask: getPostalCodeMaskRegex(watchedCountry) }}
          required
        />
      </Grid>
      <Grid size={12}>
        <Box sx={{ mt: Spacing.XS }}>
          <BlFormCheckbox
            control={control}
            name="allowRetroactivePayment"
            label={t(
              Resources.Partners.CreatePartnerModal.AllowRetroactivePayment,
            )}
            errors={errors}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

function buildDefaults(
  partner?: PartnerDetailDto | null,
  lockedParentPartnerID?: string,
  prefill?: PartnerPrefillData,
): PartnerFormData {
  if (!partner) {
    return {
      businessName: prefill?.companyName ?? "",
      superiorPartnerId: lockedParentPartnerID ?? "",
      identificationNumber: "",
      vat: "",
      email: prefill?.email ?? "",
      phone: prefill?.phone ?? "",
      address: "",
      city: "",
      postalCode: "",
      country: "",
      allowRetroactivePayment: false,
    };
  }

  return {
    businessName: partner.companyName,
    superiorPartnerId: partner.parentPartnerID ?? lockedParentPartnerID ?? "",
    identificationNumber: partner.companyNumber,
    vat: partner.taxNumber,
    email: partner.email ?? "",
    phone: partner.phone ?? "",
    address: [
      partner.address?.street,
      partner.address?.streetNumber,
      partner.address?.orientationNumber,
    ]
      .filter(Boolean)
      .join(" "),
    city: partner.address?.municipality ?? "",
    postalCode: partner.address?.postalCode ?? "",
    country: partner.address?.countryCode ?? "",
    allowRetroactivePayment: partner.allowRetroactivePayment,
  };
}
